import React, { useEffect, useState } from 'react'

import { ThemeProvider, getTheme, getCustomTheme } from '@octane/spark'

import fallback from '../branding/brands/fallback'
import { OctaneLogger } from '../integrations/datadog/OctaneLogger'
import { applyBrandConfig } from '../utils/applyBrandConfig'
import { useBrandConfig } from './BrandConfigContextProvider'

interface BrandThemeProviderProps {
    readonly children: React.ReactNode
}

export function BrandThemeProvider({ children }: BrandThemeProviderProps) {
    const brandConfig = useBrandConfig()

    const [currentTheme, setCurrentTheme] = useState(
        getTheme(fallback.sparkThemeId),
    )

    useEffect(() => {
        try {
            applyBrandConfig(brandConfig)
        } catch (error) {
            OctaneLogger.error(
                'Failed to apply brandConfig title and favicon',
                undefined,
                error as Error,
            )
        }

        const sparkThemeConfig = brandConfig?.sparkThemeConfig
        const sparkThemeId = brandConfig?.sparkThemeId
        const hasSparkThemeConfig = sparkThemeConfig != null
        const hasSparkThemeId = sparkThemeId != null

        // Avoid calling Spark theming APIs with undefined values.
        if (!hasSparkThemeConfig && !hasSparkThemeId) {
            OctaneLogger.warn(
                'BrandThemeProvider: missing sparkThemeId and sparkThemeConfig; skipping theme update (possible missing BrandConfigContextProvider)',
                {
                    sparkThemeId,
                    sparkThemeConfig: JSON.stringify(sparkThemeConfig ?? null),
                },
            )

            return
        }

        let fetchedTheme

        try {
            if (hasSparkThemeConfig) {
                fetchedTheme = getCustomTheme(sparkThemeConfig)
            } else {
                fetchedTheme = getTheme(sparkThemeId!)
            }
        } catch (error) {
            OctaneLogger.error(
                'BrandThemeProvider: Spark theme resolution failed (getTheme/getCustomTheme threw); falling back to default theme',
                {
                    sparkThemeId,
                    sparkThemeConfig: JSON.stringify(sparkThemeConfig ?? null),
                },
                error as Error,
            )

            // It's possible the sparkThemeId was entered incorrectly, fallback
            fetchedTheme = getTheme(fallback.sparkThemeId)
        }

        // Spark's ThemeConfiguration does not expose `alert`, so getCustomTheme
        // cannot override alert.note. Apply a brand-scoped override when configured.
        if (sparkThemeConfig?.alertNoteColor) {
            fetchedTheme = {
                ...fetchedTheme,
                colors: {
                    ...fetchedTheme.colors,
                    alert: {
                        ...fetchedTheme.colors.alert,
                        note: sparkThemeConfig.alertNoteColor,
                    },
                },
            }
        }

        // Temporary fallback, `navbar` will be deprecated in the future
        setCurrentTheme(fetchedTheme)
    }, [brandConfig])

    return <ThemeProvider theme={currentTheme}>{children}</ThemeProvider>
}
